<template>
    <div :class="$style['filter']">
        <div :class="$style['filter__fieldset']">
            <div
                v-for="property in filteredProperties"
                :key="property.name"
                :class="$style['filter__field']"
            >
                <SwInputDropdown
                    v-if="property.options.length"
                    :label="property.name"
                    :value="filter[property.code]"
                    :options="property.options"
                    :compare="(optionA, optionB) => optionA.id === optionB.id"
                    :stringify="option => option.value"
                    @select="onSelect"
                >
                    <template #option="{ option }">
                        {{ option.value }}
                    </template>
                </SwInputDropdown>
            </div>
        </div>
    </div>
</template>

<script>
const byCode = code => property => property.code === code
const byValue = value => property => property.value === value

const similar = ({ code, value }) => property => byCode(code)(property) && byValue(value)(property)

const getValue = (offer, code) => offer?.properties.find(byCode(code))?.value

export default {
  name: 'OfferPicker',

  props: {
    offers: {
      type: Array,
      default: () => []
    },

    selectedOfferId: {
      type: Number,
      default: null
    }
  },

  data: () => ({
    filter: {}
  }),

  computed: {
    filteredProperties () {
      return this.properties.map((property, index) => {
        if (index === 0) {
          return property
        }

        let similarOffers = this.offers

        for (let i = 0; i < index; i++) {
          const previousProperty = this.properties[i]

          similarOffers = similarOffers.filter(offer => offer.properties.some(similar({
            code: previousProperty.code,
            value: getValue(this.selectedOffer, previousProperty.code)
          })))
        }

        const possibleValues = similarOffers.reduce((possibleValues, offer) => {
          return [...possibleValues, getValue(offer, property.code)]
        }, [])

        return {
          ...property,
          options: property.options.filter(o => possibleValues.includes(o.value))
        }
      })
    },

    properties () {
      return this.offers.reduce((properties, offer) => {
        offer.properties.forEach(({ id, code, name, value }) => {
          const option = { id, code, value }
          const property = properties.find(g => g.code === code)

          if (!property) {
            properties.push({ name, code, options: [option] })
          } else if (!property.options.some(v => v.value === value)) {
            property.options.push(option)
          }
        })

        return properties
      }, [])
    },

    selectedOffer () {
      return this.offers.find(offer => offer.id === this.selectedOfferId)
    }
  },

  watch: {
    selectedOffer: {
      immediate: true,

      handler (offer) {
        offer.properties.forEach(({ id, code, value }) => {
          this.filter = { ...this.filter, [code]: { id, code, value } }
        })
      }
    }
  },

  methods: {
    onSelect ({ id, code, value }) {
      this.filter = { ...this.filter, [code]: { id, code, value } }

      const index = this.properties.findIndex(byCode(code))
      let offer = index === 0
        ? this.offers.find(offer => offer.properties.some(p => p.value === value))
        : this.offers.find(offer => offer.properties.every((p) => {
          return this.filter[p.code].value === p.value
        }))

      if (!offer) {
        offer = this.offers.find(offer => offer.properties.slice(0, index).every((p) => {
          return this.filter[p.code].value === p.value
        }))
      }

      if (offer) {
        this.$emit('select', offer.id)
      }
    }
  }
}
</script>

<style lang="less" module>
@import (reference) "./assets/Web.less";

.filter {
  padding: 30px 0 0 0;
  line-height: 24px;

  &__fieldset {
    display: flex;
    flex-wrap: wrap;
    margin: 8px -8px;
  }

  &__field {
    margin: 8px;

    @media screen and (max-width: @bp-mobile-small) {
      width: 100%;
    }
  }
}
</style>
